import { Box, ButtCon } from "@biom3/react";
import { useCallback, useEffect, useRef, useState } from "react";

type HorizontalScrollProps = {
  children: React.ReactNode;
  customScrollAmount?: number;
};

export const HorizontalScroll = ({
  children,
  customScrollAmount = 0.7,
}: HorizontalScrollProps) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [isHovered, setIsHovered] = useState(false);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const updateScrollState = useCallback(() => {
    const container = scrollRef.current;
    if (!container) return;

    const { scrollLeft, scrollWidth, clientWidth } = container;
    setCanScrollLeft(scrollLeft > 0);
    setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 1);
  }, []);

  const scroll = useCallback(
    (direction: "left" | "right") => {
      const container = scrollRef.current;
      if (!container) return;

      const scrollAmount = container.clientWidth * customScrollAmount;
      container.scrollBy({
        left: direction === "left" ? -scrollAmount : scrollAmount,
        behavior: "smooth",
      });
    },
    [customScrollAmount],
  );

  useEffect(() => {
    updateScrollState();
  }, [updateScrollState]);

  useEffect(() => {
    const container = scrollRef.current;
    if (!container) return;

    container.addEventListener("scroll", updateScrollState, { passive: true });
    return () => container.removeEventListener("scroll", updateScrollState);
  }, [updateScrollState]);

  return (
    <Box
      sx={{ position: "relative" }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <Box
        domRef={scrollRef}
        sx={{
          display: "flex",
          gap: "base.spacing.x6",
          overflowX: "auto",
          "&::-webkit-scrollbar": { display: "none" },
          scrollbarWidth: "none",
        }}
      >
        {children}
      </Box>

      {isHovered && canScrollLeft && (
        <ButtCon
          icon="ArrowBackward"
          onClick={() => scroll("left")}
          sx={{
            position: "absolute",
            left: "base.spacing.x2",
            top: "50%",
            transform: "translateY(-50%)",
          }}
        />
      )}

      {isHovered && canScrollRight && (
        <ButtCon
          icon="ArrowForward"
          onClick={() => scroll("right")}
          sx={{
            position: "absolute",
            right: "base.spacing.x2",
            top: "50%",
            transform: "translateY(-50%)",
          }}
        />
      )}
    </Box>
  );
};
